import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ClearIcon from '@mui/icons-material/Clear'
import yaml from 'js-yaml'
import { artifactKindMeta } from './artifactLabels'

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

/** Full ISO instant, e.g. 2026-10-09T11:41:26.832Z */
const ISO_DATETIME_RE =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/

/** Calendar date only — must not invent a clock time */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/

const ISO_DATETIME_IN_TEXT_RE =
  /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?/g

/** YMD not followed by time designator (avoid double-touching ISO datetimes) */
const DATE_ONLY_IN_TEXT_RE = /\d{4}-\d{2}-\d{2}(?!T|\d)/g

function formatDateOnlyYmd(ymd) {
  const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return String(ymd)
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  // Local calendar construction — avoids UTC midnight shifting the day
  const local = new Date(year, month - 1, day)
  if (
    Number.isNaN(local.getTime()) ||
    local.getFullYear() !== year ||
    local.getMonth() !== month - 1 ||
    local.getDate() !== day
  ) {
    return String(ymd)
  }
  return local.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatDateTimeIso(iso) {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Convert a whole scalar if it is a date / datetime string. */
function formatIfDate(value) {
  if (value instanceof Date) {
    return formatDateTimeIso(value.toISOString())
  }
  if (typeof value !== 'string') return null
  const s = value.trim()
  if (ISO_DATETIME_RE.test(s)) return formatDateTimeIso(s)
  if (DATE_ONLY_RE.test(s)) return formatDateOnlyYmd(s)
  return null
}

/** Replace date/datetime tokens inside free text (YAML lines, etc.). */
function humanizeDatesInText(text) {
  const raw = String(text ?? '')
  return raw
    .replace(ISO_DATETIME_IN_TEXT_RE, (match) => formatDateTimeIso(match))
    .replace(DATE_ONLY_IN_TEXT_RE, (match) => formatDateOnlyYmd(match))
}

function humanizeDatesDeep(value) {
  if (value instanceof Date) return formatDateTimeIso(value.toISOString())
  if (typeof value === 'string') {
    const asDate = formatIfDate(value)
    return asDate != null ? asDate : value
  }
  if (Array.isArray(value)) return value.map(humanizeDatesDeep)
  if (isPlainObject(value)) {
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = humanizeDatesDeep(v)
    }
    return out
  }
  return value
}

function formatScalar(value) {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') return String(value)
  const asDate = formatIfDate(value)
  if (asDate != null) return asDate
  if (typeof value === 'string') return humanizeDatesInText(value)
  return String(value)
}

function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function pathKey(parts) {
  return parts.join('.')
}

function collectExpandablePaths(data, parts = [], out = []) {
  if (Array.isArray(data)) {
    if (data.length) out.push(pathKey(parts.length ? parts : ['$']))
    data.forEach((item, idx) => {
      collectExpandablePaths(item, [...parts, String(idx)], out)
    })
    return out
  }
  if (isPlainObject(data)) {
    const entries = Object.entries(data)
    if (entries.length) out.push(pathKey(parts.length ? parts : ['$']))
    for (const [key, value] of entries) {
      if (isPlainObject(value) || Array.isArray(value)) {
        collectExpandablePaths(value, [...parts, key], out)
      }
    }
  }
  return out
}

/** Paths whose key or scalar value text matches query (case-insensitive). */
function collectSearchHits(data, query, parts = [], hits = []) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return hits

  if (Array.isArray(data)) {
    data.forEach((item, idx) => {
      const next = [...parts, String(idx)]
      const label = `Item ${idx + 1}`
      if (label.toLowerCase().includes(q)) {
        hits.push({ path: pathKey(next), kind: 'label', text: label })
      }
      collectSearchHits(item, q, next, hits)
    })
    return hits
  }

  if (isPlainObject(data)) {
    for (const [key, value] of Object.entries(data)) {
      const next = [...parts, key]
      const label = humanizeKey(key)
      const nested = isPlainObject(value) || Array.isArray(value)
      if (label.toLowerCase().includes(q) || String(key).toLowerCase().includes(q)) {
        hits.push({ path: pathKey(next), kind: 'key', text: label })
      }
      if (!nested) {
        const text = formatScalar(value)
        if (text.toLowerCase().includes(q)) {
          hits.push({ path: pathKey(next), kind: 'value', text })
        }
      } else {
        collectSearchHits(value, q, next, hits)
      }
    }
    return hits
  }

  const text = formatScalar(data)
  if (text.toLowerCase().includes(q)) {
    hits.push({
      path: pathKey(parts.length ? parts : ['$']),
      kind: 'value',
      text,
    })
  }
  return hits
}

function ancestorPaths(path) {
  if (!path || path === '$') return ['$']
  const parts = path.split('.')
  const out = ['$']
  for (let i = 1; i <= parts.length; i += 1) {
    out.push(parts.slice(0, i).join('.'))
  }
  return out
}

function highlightText(text, query) {
  const raw = String(text ?? '')
  const q = String(query || '').trim()
  if (!q) return raw
  const lower = raw.toLowerCase()
  const needle = q.toLowerCase()
  const nodes = []
  let start = 0
  let idx = lower.indexOf(needle, start)
  let n = 0
  while (idx !== -1) {
    if (idx > start) nodes.push(raw.slice(start, idx))
    nodes.push(
      <Box
        key={`h-${n}`}
        component="mark"
        sx={{
          bgcolor: 'warning.light',
          color: 'inherit',
          px: 0.25,
          borderRadius: 0.5,
        }}
      >
        {raw.slice(idx, idx + q.length)}
      </Box>,
    )
    n += 1
    start = idx + q.length
    idx = lower.indexOf(needle, start)
  }
  if (start < raw.length) nodes.push(raw.slice(start))
  return nodes.length ? nodes : raw
}

function nodeSummary(value) {
  if (Array.isArray(value)) {
    return `${value.length} item${value.length === 1 ? '' : 's'}`
  }
  if (isPlainObject(value)) {
    const n = Object.keys(value).length
    return `${n} field${n === 1 ? '' : 's'}`
  }
  return ''
}

/** Nested key/value view — collapsible, searchable. */
function TreeView({
  data,
  depth = 0,
  pathParts = [],
  collapsed,
  onToggle,
  searchQuery,
  activePath,
  nodeRefs,
}) {
  const selfPath = pathKey(pathParts.length ? pathParts : ['$'])

  if (Array.isArray(data)) {
    if (!data.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          (empty list)
        </Typography>
      )
    }
    const isCollapsed = collapsed.has(selfPath)
    // Root lists own their collapse; nested lists are gated by the parent header.
    const showChildren = depth > 0 || !isCollapsed
    return (
      <Stack spacing={0.5} sx={{ pl: depth ? 0.5 : 0 }}>
        {depth === 0 ? (
          <CollapseHeader
            label="List"
            summary={nodeSummary(data)}
            expanded={!isCollapsed}
            onToggle={() => onToggle(selfPath)}
            active={activePath === selfPath}
            path={selfPath}
            nodeRefs={nodeRefs}
          />
        ) : null}
        {showChildren
          ? data.map((item, idx) => {
              const childParts = [...pathParts, String(idx)]
              const childPath = pathKey(childParts)
              const nested = isPlainObject(item) || Array.isArray(item)
              const childCollapsed = collapsed.has(childPath)
              return (
                <Box
                  key={idx}
                  ref={(el) => {
                    if (nodeRefs) nodeRefs.current[childPath] = el
                  }}
                  sx={{
                    pl: 1,
                    borderLeft: '2px solid',
                    borderColor:
                      activePath === childPath ? 'warning.main' : 'divider',
                    bgcolor:
                      activePath === childPath
                        ? 'rgba(251, 191, 36, 0.12)'
                        : 'transparent',
                    borderRadius: 0.5,
                  }}
                >
                  {nested ? (
                    <>
                      <CollapseHeader
                        label={`Item ${idx + 1}`}
                        summary={nodeSummary(item)}
                        expanded={!childCollapsed}
                        onToggle={() => onToggle(childPath)}
                        active={activePath === childPath}
                        searchQuery={searchQuery}
                      />
                      {!childCollapsed ? (
                        <Box sx={{ mt: 0.25, pl: 0.5 }}>
                          <TreeView
                            data={item}
                            depth={depth + 1}
                            pathParts={childParts}
                            collapsed={collapsed}
                            onToggle={onToggle}
                            searchQuery={searchQuery}
                            activePath={activePath}
                            nodeRefs={nodeRefs}
                          />
                        </Box>
                      ) : null}
                    </>
                  ) : (
                    <Typography variant="body2">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        component="span"
                        sx={{ mr: 1 }}
                      >
                        Item {idx + 1}
                      </Typography>
                      {highlightText(formatScalar(item), searchQuery)}
                    </Typography>
                  )}
                </Box>
              )
            })
          : null}
      </Stack>
    )
  }

  if (isPlainObject(data)) {
    const entries = Object.entries(data)
    if (!entries.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          (empty)
        </Typography>
      )
    }

    // Root object: no outer collapse chrome; children collapse individually.
    return (
      <Stack spacing={0.5} sx={{ pl: depth ? 0.5 : 0 }}>
        {entries.map(([key, value]) => {
          const childParts = [...pathParts, key]
          const childPath = pathKey(childParts)
          const nested = isPlainObject(value) || Array.isArray(value)
          const childCollapsed = collapsed.has(childPath)
          const isActive = activePath === childPath

          return (
            <Box
              key={key}
              ref={(el) => {
                if (nodeRefs) nodeRefs.current[childPath] = el
              }}
              sx={{
                borderRadius: 0.5,
                bgcolor: isActive ? 'rgba(251, 191, 36, 0.14)' : 'transparent',
                outline: isActive ? '1px solid' : 'none',
                outlineColor: 'warning.main',
                px: isActive ? 0.75 : 0,
                py: isActive ? 0.25 : 0,
              }}
            >
              {nested ? (
                <>
                  <CollapseHeader
                    label={humanizeKey(key)}
                    summary={nodeSummary(value)}
                    expanded={!childCollapsed}
                    onToggle={() => onToggle(childPath)}
                    active={isActive}
                    searchQuery={searchQuery}
                  />
                  {!childCollapsed ? (
                    <Box
                      sx={{
                        mt: 0.25,
                        ml: 0.5,
                        pl: 1.25,
                        borderLeft: '2px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <TreeView
                        data={value}
                        depth={depth + 1}
                        pathParts={childParts}
                        collapsed={collapsed}
                        onToggle={onToggle}
                        searchQuery={searchQuery}
                        activePath={activePath}
                        nodeRefs={nodeRefs}
                      />
                    </Box>
                  ) : null}
                </>
              ) : (
                <Box>
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{ fontWeight: 600, color: 'primary.main', mr: 1 }}
                  >
                    {highlightText(humanizeKey(key), searchQuery)}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="span"
                    color="text.primary"
                  >
                    {highlightText(formatScalar(value), searchQuery)}
                  </Typography>
                </Box>
              )}
            </Box>
          )
        })}
      </Stack>
    )
  }

  return (
    <Typography variant="body2">
      {highlightText(formatScalar(data), searchQuery)}
    </Typography>
  )
}

function CollapseHeader({
  label,
  summary,
  expanded,
  onToggle,
  active,
  searchQuery,
  path,
  nodeRefs,
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.25}
      ref={
        path
          ? (el) => {
              if (nodeRefs) nodeRefs.current[path] = el
            }
          : undefined
      }
      sx={{
        cursor: 'pointer',
        userSelect: 'none',
        borderRadius: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
        py: 0.15,
      }}
      onClick={onToggle}
    >
      <Box
        sx={{
          display: 'flex',
          color: 'text.secondary',
          fontSize: 18,
          lineHeight: 1,
        }}
      >
        {expanded ? (
          <ExpandMoreIcon fontSize="inherit" />
        ) : (
          <ChevronRightIcon fontSize="inherit" />
        )}
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: active ? 'warning.dark' : 'primary.main',
        }}
      >
        {highlightText(label, searchQuery)}
      </Typography>
      {summary ? (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
          {expanded ? '' : `(${summary})`}
        </Typography>
      ) : null}
    </Stack>
  )
}

function SearchBar({
  value,
  onChange,
  matchCount,
  matchIndex,
  onPrev,
  onNext,
  placeholder = 'Search…',
}) {
  const hasQuery = Boolean(String(value || '').trim())
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            if (e.shiftKey) onPrev?.()
            else onNext?.()
          }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: hasQuery ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label="Clear search"
                onClick={() => onChange('')}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      {hasQuery ? (
        <Chip
          size="small"
          label={
            matchCount
              ? `${Math.min(matchIndex + 1, matchCount)} of ${matchCount}`
              : '0 results'
          }
          color={matchCount ? 'warning' : 'default'}
          variant={matchCount ? 'filled' : 'outlined'}
        />
      ) : null}
      <Tooltip title="Previous match (Shift+Enter)">
        <span>
          <IconButton
            size="small"
            disabled={!matchCount}
            onClick={onPrev}
            aria-label="Previous match"
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Next match (Enter)">
        <span>
          <IconButton
            size="small"
            disabled={!matchCount}
            onClick={onNext}
            aria-label="Next match"
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}

/** Lightweight YAML coloring with fold + search. */
function YamlBlock({
  text,
  searchQuery,
  activeLineIndex,
  lineRefs,
  collapsedBlocks,
  onToggleBlock,
}) {
  const lines = String(text || '').split('\n')

  const indentOf = (line) => {
    const m = line.match(/^(\s*)/)
    return m ? m[1].length : 0
  }

  const isFoldable = (i) => {
    const line = lines[i]
    if (!/:\s*$/.test(line) && !/^(\s*)-\s/.test(line)) return false
    const base = indentOf(line)
    for (let j = i + 1; j < lines.length; j += 1) {
      if (!lines[j].trim()) continue
      return indentOf(lines[j]) > base
    }
    return false
  }

  const hidden = useMemo(() => {
    const set = new Set()
    const getIndent = (line) => {
      const m = String(line).match(/^(\s*)/)
      return m ? m[1].length : 0
    }
    for (const start of collapsedBlocks) {
      if (start < 0 || start >= lines.length) continue
      const base = getIndent(lines[start])
      for (let j = start + 1; j < lines.length; j += 1) {
        if (!lines[j].trim()) {
          let k = j + 1
          while (k < lines.length && !lines[k].trim()) k += 1
          if (k >= lines.length || getIndent(lines[k]) <= base) break
          set.add(j)
          continue
        }
        if (getIndent(lines[j]) <= base) break
        set.add(j)
      }
    }
    return set
  }, [collapsedBlocks, text])

  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: 1,
        bgcolor: '#0F172A',
        color: '#E2E8F0',
        fontFamily:
          'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 13,
        lineHeight: 1.55,
        overflow: 'auto',
        maxHeight: '60vh',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {lines.map((line, i) => {
        if (hidden.has(i)) return null
        const foldable = isFoldable(i)
        const folded = collapsedBlocks.has(i)
        const isActive = activeLineIndex === i
        const comment = line.match(/^(\s*)(#.*)$/)
        const keyVal = line.match(/^(\s*)([^:#]+):(.*)$/)

        const rowSx = {
          display: 'flex',
          alignItems: 'flex-start',
          bgcolor: isActive ? 'rgba(251, 191, 36, 0.22)' : 'transparent',
          borderRadius: 0.5,
          outline: isActive ? '1px solid #FBBF24' : 'none',
        }

        const gutter = (
          <Box
            component="span"
            sx={{
              width: 18,
              flexShrink: 0,
              color: '#64748B',
              cursor: foldable ? 'pointer' : 'default',
              userSelect: 'none',
              display: 'inline-flex',
              justifyContent: 'center',
            }}
            onClick={foldable ? () => onToggleBlock(i) : undefined}
          >
            {foldable ? (folded ? '▸' : '▾') : ' '}
          </Box>
        )

        const content = (() => {
          if (comment) {
            return (
              <Box component="span" sx={{ color: '#64748B' }}>
                {highlightText(line, searchQuery)}
              </Box>
            )
          }
          if (keyVal) {
            const [, indent, key, rest] = keyVal
            return (
              <Box component="span">
                <Box component="span">{indent}</Box>
                <Box component="span" sx={{ color: '#93C5FD' }}>
                  {highlightText(key, searchQuery)}
                </Box>
                <Box component="span" sx={{ color: '#94A3B8' }}>
                  :
                </Box>
                <Box
                  component="span"
                  sx={{
                    color: /^\s*-?\d/.test(rest)
                      ? '#FBBF24'
                      : /^\s*(true|false|null)\s*$/i.test(rest)
                        ? '#C084FC'
                        : '#86EFAC',
                  }}
                >
                  {highlightText(rest, searchQuery)}
                  {folded ? (
                    <Box component="span" sx={{ color: '#64748B', ml: 1 }}>
                      …
                    </Box>
                  ) : null}
                </Box>
              </Box>
            )
          }
          return (
            <Box component="span" sx={{ color: '#CBD5E1' }}>
              {highlightText(line || ' ', searchQuery)}
              {folded ? (
                <Box component="span" sx={{ color: '#64748B', ml: 1 }}>
                  …
                </Box>
              ) : null}
            </Box>
          )
        })()

        return (
          <Box
            component="div"
            key={i}
            ref={(el) => {
              if (lineRefs) lineRefs.current[i] = el
            }}
            sx={rowSx}
          >
            {gutter}
            {content}
          </Box>
        )
      })}
    </Box>
  )
}

function yamlLineHits(text, query) {
  const q = String(query || '').trim().toLowerCase()
  if (!q) return []
  return String(text || '')
    .split('\n')
    .map((line, i) => (line.toLowerCase().includes(q) ? i : -1))
    .filter((i) => i >= 0)
}

/**
 * Same-page artifact viewer for CS audit rows.
 * Default: nested readable tree. Optional: colored YAML.
 * Both modes support collapse + search with match navigation.
 */
export default function ArtifactViewerDialog({
  open,
  onClose,
  kind,
  payload,
  actionMeta,
  loading = false,
  error = '',
}) {
  const meta = artifactKindMeta(kind)
  const [mode, setMode] = useState('readable')
  const [search, setSearch] = useState('')
  const [matchIndex, setMatchIndex] = useState(0)
  const [collapsed, setCollapsed] = useState(() => new Set())
  const [yamlCollapsed, setYamlCollapsed] = useState(() => new Set())
  const nodeRefs = useRef({})
  const lineRefs = useRef({})

  const displayPayload = useMemo(
    () => (payload == null ? null : humanizeDatesDeep(payload)),
    [payload],
  )

  const yamlText = useMemo(() => {
    if (displayPayload == null) return ''
    try {
      return yaml.dump(displayPayload, {
        indent: 2,
        lineWidth: 100,
        noRefs: true,
        sortKeys: false,
      })
    } catch {
      return humanizeDatesInText(String(payload))
    }
  }, [displayPayload, payload])

  const allExpandable = useMemo(
    () => (payload != null ? collectExpandablePaths(payload) : []),
    [payload],
  )

  const treeHits = useMemo(
    () =>
      displayPayload != null && search.trim()
        ? collectSearchHits(displayPayload, search)
        : [],
    [displayPayload, search],
  )

  const yamlHits = useMemo(
    () => (search.trim() ? yamlLineHits(yamlText, search) : []),
    [yamlText, search],
  )

  const hits = mode === 'yaml' ? yamlHits : treeHits
  const matchCount = hits.length
  const safeIndex = matchCount ? matchIndex % matchCount : 0

  // Reset search / collapse when dialog payload changes
  useEffect(() => {
    if (!open) return
    setSearch('')
    setMatchIndex(0)
    // Collapse deep nodes by default (depth > 1) for navigability
    const deep = new Set()
    for (const p of allExpandable) {
      const depth = p === '$' ? 0 : p.split('.').length
      if (depth > 1) deep.add(p)
    }
    setCollapsed(deep)
    setYamlCollapsed(new Set())
    nodeRefs.current = {}
    lineRefs.current = {}
  }, [open, payload, allExpandable])

  useEffect(() => {
    setMatchIndex(0)
  }, [search, mode])

  const expandAncestorsForPath = useCallback((path) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      for (const p of ancestorPaths(path)) {
        next.delete(p)
      }
      return next
    })
  }, [])

  // Expand + scroll to active tree match
  useEffect(() => {
    if (mode !== 'readable' || !matchCount) return
    const hit = treeHits[safeIndex]
    if (!hit) return
    expandAncestorsForPath(hit.path)
    const t = window.setTimeout(() => {
      const el = nodeRefs.current[hit.path]
      el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    }, 50)
    return () => window.clearTimeout(t)
  }, [
    mode,
    matchCount,
    safeIndex,
    treeHits,
    expandAncestorsForPath,
  ])

  // Reveal + scroll to active YAML line (unfold any collapsed ancestors)
  useEffect(() => {
    if (mode !== 'yaml' || !matchCount) return
    const line = yamlHits[safeIndex]
    if (line == null) return
    const lines = yamlText.split('\n')
    const indentOf = (ln) => {
      const m = String(ln).match(/^(\s*)/)
      return m ? m[1].length : 0
    }
    setYamlCollapsed((prev) => {
      if (!prev.size) return prev
      const next = new Set(prev)
      for (const start of prev) {
        if (start >= line) continue
        const base = indentOf(lines[start])
        let hidesTarget = false
        for (let j = start + 1; j < lines.length; j += 1) {
          if (!lines[j].trim()) continue
          if (indentOf(lines[j]) <= base) break
          if (j === line) {
            hidesTarget = true
            break
          }
        }
        if (hidesTarget) next.delete(start)
      }
      return next
    })
    const t = window.setTimeout(() => {
      const el = lineRefs.current[line]
      el?.scrollIntoView?.({ block: 'center', behavior: 'smooth' })
    }, 50)
    return () => window.clearTimeout(t)
  }, [mode, matchCount, safeIndex, yamlHits, yamlText])

  const togglePath = (path) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const toggleYamlBlock = (lineIndex) => {
    setYamlCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(lineIndex)) next.delete(lineIndex)
      else next.add(lineIndex)
      return next
    })
  }

  const expandAll = () => {
    if (mode === 'yaml') setYamlCollapsed(new Set())
    else setCollapsed(new Set())
  }

  const collapseAll = () => {
    if (mode === 'yaml') {
      const foldable = new Set()
      const lines = yamlText.split('\n')
      const indentOf = (line) => {
        const m = line.match(/^(\s*)/)
        return m ? m[1].length : 0
      }
      for (let i = 0; i < lines.length; i += 1) {
        const base = indentOf(lines[i])
        let hasChild = false
        for (let j = i + 1; j < lines.length; j += 1) {
          if (!lines[j].trim()) continue
          hasChild = indentOf(lines[j]) > base
          break
        }
        if (hasChild && (/:\s*$/.test(lines[i]) || /^(\s*)-\s/.test(lines[i]))) {
          foldable.add(i)
        }
      }
      setYamlCollapsed(foldable)
    } else {
      setCollapsed(new Set(allExpandable))
    }
  }

  const goNext = () => {
    if (!matchCount) return
    setMatchIndex((i) => (i + 1) % matchCount)
  }
  const goPrev = () => {
    if (!matchCount) return
    setMatchIndex((i) => (i - 1 + matchCount) % matchCount)
  }

  const activePath =
    mode === 'readable' && matchCount ? treeHits[safeIndex]?.path : null
  const activeLineIndex =
    mode === 'yaml' && matchCount ? yamlHits[safeIndex] : -1

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          {meta.label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {meta.description}
        </Typography>
        {actionMeta ? (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.5 }}
          >
            {actionMeta}
          </Typography>
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={1}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, v) => v && setMode(v)}
            >
              <ToggleButton value="readable" sx={{ textTransform: 'none' }}>
                Readable
              </ToggleButton>
              <ToggleButton value="yaml" sx={{ textTransform: 'none' }}>
                YAML
              </ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Expand all">
                <IconButton size="small" onClick={expandAll} aria-label="Expand all">
                  <UnfoldMoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse all">
                <IconButton
                  size="small"
                  onClick={collapseAll}
                  aria-label="Collapse all"
                >
                  <UnfoldLessIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <SearchBar
            value={search}
            onChange={setSearch}
            matchCount={matchCount}
            matchIndex={safeIndex}
            onPrev={goPrev}
            onNext={goNext}
            placeholder={
              mode === 'yaml'
                ? 'Search YAML text…'
                : 'Search fields and values…'
            }
          />
        </Stack>

        {loading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : payload == null ? (
          <Typography variant="body2" color="text.secondary">
            No details available for this artifact.
          </Typography>
        ) : mode === 'yaml' ? (
          <YamlBlock
            text={yamlText}
            searchQuery={search}
            activeLineIndex={activeLineIndex}
            lineRefs={lineRefs}
            collapsedBlocks={yamlCollapsed}
            onToggleBlock={toggleYamlBlock}
          />
        ) : (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: '#F8FAFC',
              border: '1px solid',
              borderColor: 'divider',
              maxHeight: '60vh',
              overflow: 'auto',
            }}
          >
            <TreeView
              data={displayPayload}
              collapsed={collapsed}
              onToggle={togglePath}
              searchQuery={search}
              activePath={activePath}
              nodeRefs={nodeRefs}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
